import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { TableCursor, TableEditor } from "slate-table";
import { useState } from "react";
import styled from "styled-components";
import { color } from "@m/alchemy-theme";
import { AddIcon } from "../../icons/AddIcon";
import { ArrowDownIcon } from "../../icons/ArrowDownIcon";
import { ArrowUpIcon } from "../../icons/ArrowUpIcon";
import { ArrowLeftIcon } from "../../icons/ArrowLeftIcon";
import { ArrowRightIcon } from "../../icons/ArrowRightIcon";
import { BoldIcon } from "../../icons/BoldIcon";
import { BulletedListIcon } from "../../icons/BulletedListIcon";
import { CancelXIcon } from "../../icons/CancelXIcon";
import { CenterAlignedIcon } from "../../icons/CenterAlignedIcon";
// import { CodeIcon } from "../../icons/CodeIcon";
import { ColorIcon } from "../../icons/ColorIcon";
import { DeleteIcon } from "../../icons/DeleteIcon";
import { ExdentIcon } from "../../icons/ExdentIcon";
import { FontSizeIcon } from "../../icons/FontSizeIcon";
import { IndentIcon } from "../../icons/IndentIcon";
import { ItalicIcon } from "../../icons/ItalicIcon";
import { LeftJustifiedIcon } from "../../icons/LeftJustifiedIcon";
import { MinusIcon } from "../../icons/MinusIcon";
import { OrderedListIcon } from "../../icons/OrderedListIcon";
import { ParagraphIcon } from "../../icons/ParagraphIcon";
import { RedoIcon } from "../../icons/RedoIcon";
import { RemoveStylesIcon } from "../../icons/RemoveStylesIcon";
import { RightJustifiedIcon } from "../../icons/RightJustifiedIcon";
import { TextOptionIcon } from "../../icons/TextOptionIcon";
import { UnderlineIcon } from "../../icons/UnderlineIcon";
import { UndoIcon } from "../../icons/UndoIcon";
import { TableIcon } from "../../icons/TableIcon";
import { PopperBox } from "../../popper/PopperBox";
import { MenuList } from "../../menu-list/MenuList";
import { MenuListItem } from "../../menu-list-item/index";
import { Text } from "../../text/Text";
import { FocusList } from "../../utilities/focus-list/FocusList";
import { ColorPicker } from "../color-picker/ColorPicker";
import { clearStyles, FONT_FAMILY, FONT_SIZES, getCurrentList, getSelectedColor, indent, isActiveTextAlign, isBlockActive, isFontFamilyActive, isFontSizeActive, outdent, setColor, setFontFamily, setFontSize, setTextAlign, toggleBlock, toggleFormat, toggleList } from "./utilities/index";
import { imageToolbarControl as image } from "./plugins/images/imageToolbarControl";
import { linkToolbarControl as link } from "./plugins/links/LinkToolbarControl";
export const toolbarGroupMap = [
    ["typeStyle", "fontSize", "fontFamily", "color"],
    ["bold", "italic", "underline"],
    ["alignLeft", "alignCenter", "alignRight"],
    ["orderedList", "unorderedList", "outdent", "indent"],
    ["image", "link"],
    ["table"],
    ["clearFormatting", "undo", "redo"]
];
export const sortControls = (controlsList) => {
    const customControls = controlsList.filter((c) => typeof c === "object" && !availableControls[c.name]);
    const groupedControls = toolbarGroupMap
        .map((group) => {
        const controlObjects = group
            .map((controlName) => {
            const foundControl = controlsList.find((c) => typeof c === "string" ? c === controlName : c.name === controlName);
            if (!foundControl) {
                return false;
            }
            if (typeof foundControl === "string") {
                return controlsList.includes(controlName) ? availableControls[controlName] || false : false;
            }
            // Merge custom control with default control
            const mergedControl = { ...availableControls[controlName], ...foundControl };
            if (mergedControl.icon === undefined) {
                throw new Error(`Icon is missing for toolbar control "${controlName}"`);
            }
            return mergedControl;
        })
            .filter(Boolean);
        return controlObjects.length ? controlObjects : false;
    })
        .filter(Boolean);
    if (customControls.length) {
        // @ts-expect-error
        groupedControls.push(customControls);
    }
    // icon is required for all controls, but optional custom config overrides. If an icon is missing, throw an error
    return groupedControls;
};
const blockTypeStyleMap = {
    headingOne: "headerXL",
    headingTwo: "headerL",
    headingThree: "headerM",
    headingFour: "headerS",
    headingFive: "subHeaderM",
    headingSix: "subHeaderS",
    // quote: "bodyL",
    paragraph: "bodyS"
};
export const FakeIcon = styled.div `
  border: 1px solid ${color("base")};
  border-radius: 2px;
`;
// Fix for jumpiness when opening the menu
export const TableMenuList = styled(MenuList) `
  overflow: hidden;
  min-width: 200px;
`;
export const TableToolbarControl = (props) => {
    const { editor, closeAndFocusEditor, stringMap } = props;
    const [isColumnExpanded, setIsColumnExpanded] = useState(false);
    const [isRowExpanded, setIsRowExpanded] = useState(false);
    return (_jsx(PopperBox, { hasPadding: false, children: _jsxs(FocusList, { children: [_jsx(TableMenuList, { mode: "menu", children: TableCursor.isInTable(editor) ? (_jsx(MenuListItem, { label: stringMap.tableDelete, icon: _jsx(DeleteIcon, { "aria-hidden": "true" }), disabled: !TableCursor.isInTable(editor), onClick: () => {
                            TableEditor.removeTable(editor);
                            closeAndFocusEditor();
                        } })) : (_jsx(MenuListItem, { label: stringMap.tableInsert, icon: _jsx(AddIcon, { "aria-hidden": "true" }), disabled: TableCursor.isInTable(editor), onClick: () => {
                            TableEditor.insertTable(editor);
                            closeAndFocusEditor();
                        } })) }), _jsxs(TableMenuList, { mode: "menu", children: [_jsx(MenuListItem, { label: stringMap.tableColumns, icon: _jsx(FakeIcon, { style: { width: 8, height: 16 }, "aria-hidden": "true" }), disabled: !TableCursor.isInTable(editor), selected: false, isExpanded: isColumnExpanded, onClick: () => setIsColumnExpanded(!isColumnExpanded) }), _jsxs(MenuListItem.Group, { isExpanded: isColumnExpanded, children: [_jsx(MenuListItem, { label: stringMap.tableColumnInsertBefore, icon: _jsx(ArrowLeftIcon, { "aria-hidden": "true" }), onClick: () => {
                                        TableEditor.insertColumn(editor, { before: true });
                                        closeAndFocusEditor();
                                    } }), _jsx(MenuListItem, { label: stringMap.tableColumnInsertAfter, icon: _jsx(ArrowRightIcon, { "aria-hidden": "true" }), onClick: () => {
                                        TableEditor.insertColumn(editor, { before: false });
                                        closeAndFocusEditor();
                                    } }), _jsx(MenuListItem, { label: stringMap.tableColumnDelete, icon: _jsx(MinusIcon, { "aria-hidden": "true" }), onClick: () => {
                                        TableEditor.removeColumn(editor);
                                        closeAndFocusEditor();
                                    } })] })] }), _jsxs(TableMenuList, { mode: "menu", children: [_jsx(MenuListItem, { label: stringMap.tableRows, icon: _jsx(FakeIcon, { style: { width: 16, height: 8, marginTop: 4 }, "aria-hidden": "true" }), disabled: !TableCursor.isInTable(editor), selected: false, isExpanded: isRowExpanded, onClick: () => setIsRowExpanded(!isRowExpanded) }), _jsxs(MenuListItem.Group, { isExpanded: isRowExpanded, children: [_jsx(MenuListItem, { label: stringMap.tableRowInsertAbove, icon: _jsx(ArrowUpIcon, { "aria-hidden": "true" }), onClick: () => {
                                        TableEditor.insertRow(editor, { before: true });
                                        closeAndFocusEditor();
                                    } }), _jsx(MenuListItem, { label: stringMap.tableRowInsertBelow, icon: _jsx(ArrowDownIcon, { "aria-hidden": "true" }), onClick: () => {
                                        TableEditor.insertRow(editor, { before: false });
                                        closeAndFocusEditor();
                                    } }), _jsx(MenuListItem, { label: stringMap.tableRowDelete, icon: _jsx(MinusIcon, { "aria-hidden": "true" }), onClick: () => {
                                        TableEditor.removeRow(editor);
                                        closeAndFocusEditor();
                                    } })] })] })] }) }));
};
export const availableControls = {
    bold: {
        name: "bold",
        icon: _jsx(BoldIcon, { "aria-hidden": "true" }),
        callback: (editor) => {
            toggleFormat(editor, "bold");
        }
    },
    italic: {
        name: "italic",
        icon: _jsx(ItalicIcon, { "aria-hidden": "true" }),
        callback: (editor) => {
            toggleFormat(editor, "italic");
        }
    },
    underline: {
        name: "underline",
        icon: _jsx(UnderlineIcon, { "aria-hidden": "true" }),
        callback: (editor) => {
            toggleFormat(editor, "underline");
        }
    },
    alignLeft: {
        name: "alignLeft",
        icon: _jsx(LeftJustifiedIcon, { "aria-hidden": "true" }),
        isActive: (editor) => isActiveTextAlign(editor, "left"),
        callback: (editor) => {
            setTextAlign(editor, "left");
        }
    },
    alignCenter: {
        name: "alignCenter",
        icon: _jsx(CenterAlignedIcon, { "aria-hidden": "true" }),
        isActive: (editor) => isActiveTextAlign(editor, "center"),
        callback: (editor) => {
            setTextAlign(editor, "center");
        }
    },
    alignRight: {
        name: "alignRight",
        icon: _jsx(RightJustifiedIcon, { "aria-hidden": "true" }),
        isActive: (editor) => isActiveTextAlign(editor, "right"),
        callback: (editor) => {
            setTextAlign(editor, "right");
        }
    },
    orderedList: {
        name: "orderedList",
        icon: _jsx(OrderedListIcon, { "aria-hidden": "true" }),
        isActive: (editor) => getCurrentList(editor)?.type === "orderedList",
        callback: (editor) => {
            toggleList(editor, "orderedList");
        }
    },
    unorderedList: {
        name: "unorderedList",
        icon: _jsx(BulletedListIcon, { "aria-hidden": "true" }),
        isActive: (editor) => getCurrentList(editor)?.type === "unorderedList",
        callback: (editor) => {
            toggleList(editor, "unorderedList");
        }
    },
    indent: {
        name: "indent",
        icon: _jsx(IndentIcon, { "aria-hidden": "true" }),
        callback: (editor) => {
            indent(editor);
        }
    },
    image,
    // source: { name: "source", label: "Source", icon: <CodeIcon aria-hidden="true" />, callback: (editor: Editor) => {} },
    link,
    // fontFamily: {
    //   name: "fontFamily",
    //   label: "Font-family",
    //   icon: <TextOptionIcon aria-hidden="true" />,
    //   callback: (editor: Editor) => {}
    // },
    table: {
        name: "table",
        label: "Table",
        icon: _jsx(TableIcon, { "aria-hidden": "true" }),
        popover: ({ editor, closeAndFocusEditor, stringMap }) => (_jsx(TableToolbarControl, { editor: editor, closeAndFocusEditor: closeAndFocusEditor, stringMap: stringMap }))
    },
    undo: {
        name: "undo",
        icon: _jsx(UndoIcon, { "aria-hidden": "true" }),
        callback: (editor) => {
            editor.undo();
        }
    },
    redo: {
        name: "redo",
        icon: _jsx(RedoIcon, { "aria-hidden": "true" }),
        callback: (editor) => {
            editor.redo();
        }
    },
    color: {
        name: "color",
        icon: _jsx(ColorIcon, { "aria-hidden": "true" }),
        popover: ({ editor, closeAndFocusEditor }) => (_jsx(PopperBox, { hasPadding: false, children: _jsx(ColorPicker, { mode: "inline", selectedColor: getSelectedColor(editor), onCancel: () => {
                    closeAndFocusEditor();
                }, onApply: ({ color }) => {
                    setColor(editor, color);
                    closeAndFocusEditor();
                } }) }))
    },
    fontFamily: {
        name: "fontFamily",
        icon: _jsx(TextOptionIcon, { "aria-hidden": "true" }),
        popover: ({ editor, closeAndFocusEditor, stringMap }) => (_jsx(PopperBox, { hasPadding: false, children: _jsx(FocusList, { children: _jsx(MenuList, { mode: "select", items: [
                        FONT_FAMILY.map((fontFamily) => ({
                            // label: props.stringMap[elementType],
                            label: (_jsx(Text, { typeStyle: "bodyS", style: { fontFamily }, children: fontFamily })),
                            selected: isFontFamilyActive(editor, fontFamily),
                            onMouseDown: (e) => {
                                e.preventDefault();
                            },
                            onClick: (e) => {
                                e.preventDefault();
                                setFontFamily(editor, fontFamily);
                                closeAndFocusEditor();
                            }
                        })),
                        {
                            icon: _jsx(CancelXIcon, { "aria-hidden": "true" }),
                            label: stringMap.resetToDefault,
                            onMouseDown: (e) => {
                                e.preventDefault();
                            },
                            onClick: (e) => {
                                e.preventDefault();
                                setFontFamily(editor, undefined);
                                closeAndFocusEditor();
                            }
                        }
                    ].flat() }) }) }))
    },
    fontSize: {
        name: "fontSize",
        icon: _jsx(FontSizeIcon, { "aria-hidden": "true" }),
        popover: ({ editor, closeAndFocusEditor, stringMap }) => (_jsx(PopperBox, { hasPadding: false, children: _jsx(FocusList, { children: _jsx(MenuList, { mode: "select", items: [
                        FONT_SIZES.map((fontSize) => ({
                            // label: props.stringMap[elementType],
                            label: (_jsxs(Text, { typeStyle: "bodyS", style: { fontSize }, children: [fontSize, "px"] })),
                            selected: isFontSizeActive(editor, fontSize),
                            onMouseDown: (e) => {
                                e.preventDefault();
                            },
                            onClick: (e) => {
                                e.preventDefault();
                                setFontSize(editor, fontSize);
                                closeAndFocusEditor();
                            }
                        })),
                        {
                            icon: _jsx(CancelXIcon, { "aria-hidden": "true" }),
                            label: stringMap.resetToDefault,
                            onMouseDown: (e) => {
                                e.preventDefault();
                            },
                            onClick: (e) => {
                                e.preventDefault();
                                setFontSize(editor, undefined);
                                closeAndFocusEditor();
                            }
                        }
                    ].flat() }) }) }))
    },
    outdent: {
        name: "outdent",
        icon: _jsx(ExdentIcon, { "aria-hidden": "true" }),
        callback: (editor) => {
            outdent(editor);
        }
    },
    typeStyle: {
        name: "typeStyle",
        icon: _jsx(ParagraphIcon, { "aria-hidden": "true" }),
        popover: ({ editor, closeAndFocusEditor, stringMap }) => (_jsx(PopperBox, { hasPadding: false, children: _jsx(FocusList, { children: _jsx(MenuList, { mode: "select", items: Object.entries(blockTypeStyleMap).map(([elementType, typeStyle]) => ({
                        label: _jsx(Text, { typeStyle: typeStyle, children: stringMap[elementType] }),
                        selected: isBlockActive(editor, elementType),
                        onMouseDown: (e) => {
                            e.preventDefault();
                        },
                        onClick: (e) => {
                            e.preventDefault();
                            // remove styles when selecting a heading level
                            clearStyles(editor);
                            toggleBlock(editor, elementType);
                            closeAndFocusEditor();
                        }
                    })) }) }) }))
    },
    clearFormatting: {
        name: "clearFormatting",
        icon: _jsx(RemoveStylesIcon, { "aria-hidden": "true" }),
        callback: (editor) => {
            clearStyles(editor);
        }
    }
};
export const isControlKey = (key) => key in availableControls;
